/** Common UI primitives used across CostIQ pages. */
import { Loader2, AlertCircle } from "lucide-react";

export function PageHeader({ eyebrow, title, subtitle, actions }) {
    return (
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
                {eyebrow && <div className="eyebrow text-slate-500 mb-1.5">{eyebrow}</div>}
                <h1 className="font-display text-[28px] lg:text-[32px] font-semibold text-slate-900 leading-tight">
                    {title}
                </h1>
                {subtitle && (
                    <p className="text-sm text-slate-500 mt-1.5 max-w-2xl">{subtitle}</p>
                )}
            </div>
            {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
    );
}

export function Card({ children, className = "", testId, hover = false, ...rest }) {
    return (
        <div
            data-testid={testId}
            {...rest}
            className={[
                "rounded-lg border border-slate-200 bg-white",
                hover && "hover-lift cursor-pointer",
                className,
            ].filter(Boolean).join(" ")}
        >
            {children}
        </div>
    );
}

export function GlassCard({ children, className = "", testId }) {
    return (
        <div
            data-testid={testId}
            className={["rounded-lg border glass-card", className].join(" ")}
        >
            {children}
        </div>
    );
}

export function LoadingState({ label = "Loading" }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <div className="text-xs mt-3">{label}…</div>
        </div>
    );
}

export function EmptyState({ icon: Icon, title, description, action }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            {Icon && <Icon className="h-10 w-10 text-slate-300 mb-3" strokeWidth={1.5} />}
            <div className="text-sm font-medium text-slate-700">{title}</div>
            {description && (
                <div className="text-xs text-slate-500 mt-1.5 max-w-sm">{description}</div>
            )}
            {action && <div className="mt-4">{action}</div>}
        </div>
    );
}

export function ErrorState({ message, onRetry }) {
    return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1">
                <div className="text-sm font-medium text-red-800">Something went wrong</div>
                <div className="text-xs text-red-700 mt-1">{message}</div>
                {onRetry && (
                    <button
                        onClick={onRetry}
                        className="text-xs underline text-red-700 mt-2 hover:text-red-900"
                    >
                        Try again
                    </button>
                )}
            </div>
        </div>
    );
}

/** Pill — small uppercase status indicator. */
export function Pill({ children, tone = "slate", className = "" }) {
    const tones = {
        slate: "bg-slate-100 text-slate-600 border-slate-200",
        blue: "bg-blue-50 text-blue-700 border-blue-200",
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
        red: "bg-red-50 text-red-700 border-red-200",
        copper: "bg-[hsl(var(--copper-soft))] text-[hsl(var(--copper))] border-[hsl(var(--copper))]/30",
        solid_red: "bg-red-500 text-white border-red-500",
        violet: "bg-violet-50 text-violet-700 border-violet-200",
    };
    return <span className={`pill ${tones[tone] || tones.slate} ${className}`}>{children}</span>;
}

/** A primary copper CTA button. */
export function CopperButton({ children, className = "", ...props }) {
    return (
        <button
            className={[
                "inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md",
                "bg-[hsl(var(--copper))] text-white text-sm font-medium",
                "hover:bg-[hsl(var(--copper))]/90 transition-colors",
                "shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
                className,
            ].join(" ")}
            {...props}
        >
            {children}
        </button>
    );
}

export function SlateButton({ children, className = "", variant = "outline", ...props }) {
    const base = "inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-50";
    const variants = {
        outline: "border border-slate-200 bg-white text-slate-700 hover:bg-[hsl(var(--copper-soft))] hover:text-[hsl(var(--copper))]",
        ghost: "text-slate-700 hover:bg-slate-100",
        solid: "bg-slate-900 text-white hover:bg-slate-800 shadow-sm",
        danger: "bg-red-500 text-white hover:bg-red-600 shadow-sm",
    };
    return (
        <button className={`${base} ${variants[variant]} ${className}`} {...props}>
            {children}
        </button>
    );
}

/** Form field wrapper with label + input. */
export function Field({ label, error, helper, children, required }) {
    return (
        <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1">
                {label}
                {required && <span className="text-[hsl(var(--copper))]">*</span>}
            </label>
            {children}
            {error && <p className="text-[11px] text-red-600">{error}</p>}
            {helper && !error && <p className="text-[11px] text-slate-500">{helper}</p>}
        </div>
    );
}

export function TextInput({ className = "", ...props }) {
    return (
        <input
            type="text"
            className={[
                "w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900",
                "placeholder:text-slate-400 transition-colors",
                className,
            ].join(" ")}
            {...props}
        />
    );
}

export function NumberInput({ className = "", ...props }) {
    return (
        <input
            type="number"
            inputMode="decimal"
            className={[
                "w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900",
                "font-mono tabular-nums text-right",
                "placeholder:text-slate-400 transition-colors",
                className,
            ].join(" ")}
            {...props}
        />
    );
}

export function Select({ className = "", children, ...props }) {
    return (
        <select
            className={[
                "w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900",
                "transition-colors appearance-none pr-8",
                className,
            ].join(" ")}
            style={{ backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
            {...props}
        >
            {children}
        </select>
    );
}

/** Modal dialog. */
export function Modal({ open, onClose, title, children, footer, size = "md" }) {
    if (!open) return null;
    const widths = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-lg", xl: "max-w-2xl", "2xl": "max-w-4xl", "3xl": "max-w-6xl" };
    return (
        <>
            <div className="fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-sm" onClick={onClose} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                <div className={`${widths[size]} w-full bg-white rounded-xl shadow-2xl pointer-events-auto animate-fade-up`}>
                    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                        <h3 className="font-display text-[18px] font-semibold text-slate-900">{title}</h3>
                        <button onClick={onClose} className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center" data-testid="modal-close-btn">
                            <svg className="h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
                        </button>
                    </div>
                    <div className="px-5 py-4">{children}</div>
                    {footer && (
                        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/50 rounded-b-xl">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
